import { ZuiBaseElement } from '@zywave/zui-base';
import '@zywave/zui-icons';
import { html, nothing } from 'lit';
import { property, queryAssignedElements, query } from 'lit/decorators.js';
import { style } from './zui-tabs-css.js';
import type { ZuiTabElement } from './zui-tab.js';
import type { PropertyValues } from 'lit';

/**
 * @element zui-tabs
 *
 * @slot unnamed - Slot in `zui-tab` elements only
 *
 * @cssprop [--zui-tabs-color=var(--zui-blue)] - Highlighted color of the selected tab, impacts text and border color
 * @cssprop [--zui-tabs-fouc-scroll-button-space=3rem (48px)] - (FOUC styling only): occupies a space by default for the scroll buttons (these buttons only appear when there's width overflow); recommend finding viewport width where scroll buttons are visible, and then set this CSS custom property to `0` above that media query width
 */

export class ZuiTabsElement extends ZuiBaseElement {
  /**
   * Determines which `<zui-tab>` is selected, 0 based index
   */
  @property({ type: Number, reflect: true })
  selected = 0;

  @queryAssignedElements({ selector: 'zui-tab' })
  private _slottedTabsEls: Array<ZuiTabElement>;

  @query('#slotted-tabs-container')
  private _slottedTabsContainerEl: HTMLElement;

  @query('#selected-underline')
  private _selectedUnderlineEl: HTMLElement;

  @query('button#scroll-left')
  private _btnScrollLeftEl: HTMLElement;

  #tabColorRgbField = 'rgba(0, 0, 0, 0)';

  get #tabColorRgb() {
    return this.#tabColorRgbField;
  }
  set #tabColorRgb(value: string) {
    this.#tabColorRgbField = value;
    this.requestUpdate();
  }

  #tabsOnPageLoad = true;

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#handleWindowResize.bind(this));
  }

  firstUpdated() {
    for (const tab of this._slottedTabsEls) {
      tab.addEventListener('tabselected', this.#handleTabSelected);
      tab.addEventListener('tabsizechange', () => setTimeout(() => this.#calibrate(), 1));
    }
    this.#tabColorRgb = window?.getComputedStyle(this._selectedUnderlineEl)?.borderBottomColor;
    window.addEventListener('resize', this.#handleWindowResize.bind(this));
  }

  #windowResizeTimeout: number | null = null;

  #handleWindowResize() {
    if (this.#windowResizeTimeout) {
      cancelAnimationFrame(this.#windowResizeTimeout);
    }
    this.#windowResizeTimeout = requestAnimationFrame(() => {
      this.#calibrate();
    });
  }

  /* eslint-disable @typescript-eslint/no-explicit-any */

  async updated(changedProperties: PropertyValues) {
    if (changedProperties.has('selected')) {
      const oldVal = changedProperties.get('selected');
      if (oldVal !== undefined) {
        if (this.#tabsOnPageLoad) {
          this._selectedUnderlineEl?.classList.remove('no-animate-to');
          this.#tabsOnPageLoad = false;
        }
        const oldTab = this._slottedTabsEls[oldVal];
        if (oldTab) {
          (oldTab as any)._selected = false;
        }
        (this._slottedTabsEls[oldVal] as any)._selected = false;
      }
      if (this._slottedTabsEls[this.selected]) {
        (this._slottedTabsEls[this.selected] as any)._selected = true;
        await this._slottedTabsEls[this.selected].updateComplete;
      }
      this.#calibrate();
    }
  }

  async #onSlotChange() {
    const selectedTab = this._slottedTabsEls[this.selected];
    if (selectedTab && !(selectedTab as any)._selected) {
      (this._slottedTabsEls[this.selected] as any)._selected = true;
      await this._slottedTabsEls[this.selected].updateComplete;
      this.#calibrate();
    }
  }

  /* eslint-enable @typescript-eslint/no-explicit-any */

  #calibrate() {
    this.#determineShowScrollButtons();
    this.#placeSelectedUnderline(this._slottedTabsEls[this.selected]);
    this.#scrollToSeeUnderline();
    this.#determineScrolledToEnd();
    this.dispatchEvent(new CustomEvent('tabscalibrated', { bubbles: true, composed: true, detail: { tab: this } }));
  }

  async #scrollToSeeUnderline() {
    if (this._slottedTabsEls[0] && this._slottedTabsEls[this.selected]) {
      await this._slottedTabsEls[0].updateComplete;
      await this._slottedTabsEls[this.selected].updateComplete;
      const xOffset = this._slottedTabsEls[0].offsetLeft;
      const containerEl = this._slottedTabsContainerEl;
      const viewport = {
        get scroll() {
          return Math.round(containerEl.scrollLeft);
        },
        set scroll(value: number) {
          containerEl.scrollLeft = value;
        },
        left: containerEl.scrollLeft,
        right: containerEl.scrollLeft + containerEl.offsetWidth,
        width: containerEl.offsetWidth,
      };
      const tabEl = this._slottedTabsEls[this.selected] as ZuiTabElement;
      const tab = {
        left: tabEl?.offsetLeft - xOffset,
        right: tabEl?.offsetLeft + tabEl?.offsetWidth - xOffset,
        width: tabEl?.offsetWidth,
      };
      const adjacentTabPreview = 35;

      // if tab.width is greater than viewport.width just scroll to tab.left
      if (tab.width + adjacentTabPreview > viewport.width) {
        viewport.scroll = tab.left;
        // if tab left and right are not both between the viewport range
      } else if (tab?.left < viewport.left || tab?.right > viewport.right) {
        // if lab.left is offscreen aka less than viewport.scroll scroll left
        if (tab.left < viewport.scroll) {
          // scrollLeft = tab.left - adjacentTabPreview
          viewport.scroll = tab.left - adjacentTabPreview;
        } else {
          // else scrollLeft = tab.right + viewport width - adjacentTabPreview
          viewport.scroll = tab.right - viewport.width + adjacentTabPreview;
        }
      }
    }
  }

  static get styles() {
    return [super.styles, style];
  }

  render() {
    return html`
      <div
        class="tabs-container"
        role="tablist"
        style="--zui-tabs-color-animation-light-1: ${this.#rgbaTint(
          this.#tabColorRgb,
          20
        )} ${`; `} --zui-tabs-color-animation-light-2: ${this.#rgbaTint(this.#tabColorRgb, 13.33)}; "
      >
        ${this.#showScrollButtons
          ? html`
              <button
                @keydown="${(event: KeyboardEvent) =>
                  this.#handleScrollRepeat(event, event.target as HTMLButtonElement)}"
                @keyup="${this.#handleScrollStopRepeat}"
                @mousedown="${(event: MouseEvent) =>
                  this.#handleScrollRepeat(event, event.target as HTMLButtonElement)}"
                @mouseup="${this.#handleScrollStopRepeat}"
                @touchstart="${(event: TouchEvent) =>
                  this.#handleScrollRepeat(event, event.target as HTMLButtonElement)}"
                @touchend="${this.#handleScrollStopRepeat}"
                type="button"
                id="scroll-left"
                class="${this.#showScrollButtonLeft ? nothing : 'disabled'}"
                tabindex=${this.#showScrollButtonLeft ? nothing : '-1'}
              >
                <zui-icon icon="zui-chevron-left"></zui-icon>
              </button>
              <button
                @keydown="${(event: KeyboardEvent) =>
                  this.#handleScrollRepeat(event, event.target as HTMLButtonElement)}"
                @keyup="${this.#handleScrollStopRepeat}"
                @mousedown="${(event: MouseEvent) =>
                  this.#handleScrollRepeat(event, event.target as HTMLButtonElement)}"
                @mouseup="${this.#handleScrollStopRepeat}"
                @touchstart="${(event: TouchEvent) =>
                  this.#handleScrollRepeat(event, event.target as HTMLButtonElement)}"
                @touchend="${this.#handleScrollStopRepeat}"
                type="button"
                id="scroll-right"
                class="${this.#showScrollButtonRight ? nothing : 'disabled'}"
                tabindex=${this.#showScrollButtonRight ? nothing : '-1'}
              >
                <zui-icon icon="zui-chevron-right"></zui-icon>
              </button>
            `
          : nothing}
        <div
          id="slotted-tabs-container"
          @touchstart="${this.#handleTouchRepeat}"
          @touchend="${this.#handleTouchStopRepeat}"
        >
          <slot @slotchange="${this.#onSlotChange}"></slot>
          <div id="selected-underline" class="no-animate-to"></div>
        </div>
      </div>
    `;
  }

  #rgbaTint(rgbOrRgba: string, opacityPercentInteger: number) {
    const arr = rgbOrRgba.replace('rgb(', 'rgba(').replace(')', '').split(',');
    arr[3] = ` ${opacityPercentInteger / 100})`;
    return arr.join(',');
  }

  #showScrollButtons = false;
  #showScrollButtonLeft = false;
  #showScrollButtonRight = false;

  async #determineShowScrollButtons() {
    const containerWidth = this.offsetWidth;
    let tabsActualWidth = 0;
    for (const tab of this._slottedTabsEls) {
      tabsActualWidth += tab.offsetWidth;
    }
    tabsActualWidth > containerWidth ? (this.#showScrollButtons = true) : (this.#showScrollButtons = false);
    this.requestUpdate();
  }

  #determineScrolledToEnd(button: HTMLButtonElement | null = null) {
    // slow down a tick so values below aren't prematurely evaluated
    setTimeout(() => {
      let tabsActualWidth = 0;
      for (const tab of this._slottedTabsEls) {
        tabsActualWidth += tab.offsetWidth;
      }
      const leftOldVal = this.#showScrollButtonLeft;
      const leftCurrVal = this._slottedTabsContainerEl.scrollLeft > 0 ? true : false;
      const rightOldVal = this.#showScrollButtonRight;
      const rightCurrVal =
        Math.ceil(this._slottedTabsContainerEl.scrollLeft) >=
        Math.floor(tabsActualWidth) - Math.floor(this._slottedTabsContainerEl.offsetWidth) - 1
          ? false
          : true;

      if (leftOldVal !== leftCurrVal) {
        this.#showScrollButtonLeft = leftCurrVal;
        if (button && button.id === 'scroll-left') {
          clearInterval(this.#handleScrollInterval);
        }
        this.requestUpdate();
      }

      if (rightOldVal !== rightCurrVal) {
        this.#showScrollButtonRight = rightCurrVal;
        if (button && button.id === 'scroll-right') {
          clearInterval(this.#handleScrollInterval);
        }
        this.requestUpdate();
      }
    }, 0);
  }

  #placeSelectedUnderline(tab: ZuiTabElement) {
    if (tab) {
      const tabRect = tab.getBoundingClientRect();
      const containerRect = this.getBoundingClientRect();
      const leftButtonRect = this._btnScrollLeftEl?.getBoundingClientRect() ?? { width: 0 };
      const scrollLeftOffset = this._slottedTabsContainerEl?.scrollLeft ?? 0;
      this._selectedUnderlineEl.style.transform = `translateX(${
        tabRect.left - leftButtonRect.width - containerRect.left + scrollLeftOffset
      }px)`;
      this._selectedUnderlineEl.style.width = `${tabRect.width}px`;
    }
  }

  #handleTabSelected = (event: CustomEvent) => {
    const index = this._slottedTabsEls.indexOf(event.detail.tab);
    if (index !== this.selected) {
      this.selected = index;
    }
  };

  #handleScroll(event: MouseEvent | KeyboardEvent | TouchEvent, target: HTMLButtonElement) {
    if (
      (event instanceof KeyboardEvent && (event.key === 'Enter' || event.key === ' ')) ||
      event instanceof MouseEvent ||
      event instanceof TouchEvent
    ) {
      const ForwardOrBackward = target === this._btnScrollLeftEl ? `-` : ``;
      this._slottedTabsContainerEl.scrollLeft += Number(`${ForwardOrBackward}30`);
      this.#determineScrolledToEnd(target);
    }
  }

  #handleScrollInterval: ReturnType<typeof setTimeout>;

  #handleScrollRepeat(event: MouseEvent | KeyboardEvent | TouchEvent, target: HTMLButtonElement) {
    this.#handleScroll(event, target);
    this.#handleScrollInterval = setInterval(() => this.#handleScroll(event, target), 30);
  }

  #handleScrollStopRepeat() {
    clearInterval(this.#handleScrollInterval);
  }

  #handleTouchInterval: ReturnType<typeof setTimeout>;

  #handleTouchRepeat() {
    this.#handleTouchInterval = setInterval(() => {
      this.#determineScrolledToEnd();
    }, 100);
  }

  #handleTouchStopRepeat() {
    clearInterval(this.#handleTouchInterval);
  }
}

window.customElements.define('zui-tabs', ZuiTabsElement);

declare global {
  interface HTMLElementTagNameMap {
    'zui-tabs': ZuiTabsElement;
  }
}
